<template>
  <div class="modal fade" id="newChatModal" tabindex="-1" aria-labelledby="newChatModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="newChatModalLabel">Nova Conversa</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar" @click="resetForm"></button>
        </div>
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label required">País</label>
              <select
                v-model="form.country_code"
                class="form-select"
                required
              >
                <option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.flag }} {{ country.name }} ({{ country.dialCode }})
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label required">Número do Telefone</label>
              <div class="input-group">
                <span class="input-group-text">{{ selectedCountry.dialCode }}</span>
                <input
                  type="text"
                  v-model="form.phone_number"
                  class="form-control"
                  :placeholder="selectedCountry.placeholder"
                  v-mask="selectedCountry.mask"
                  required
                />
              </div>
              <div class="form-text">Digite o número com DDD</div>
            </div>

            <div class="mb-3">
              <label class="form-label required">Template</label>
              <select
                v-model="form.template_id"
                class="form-select"
                required
                @change="onTemplateChange"
              >
                <option value="">Selecione um template</option>
                <option
                  v-for="template in approvedTemplates"
                  :key="template.id_whatsapp_template"
                  :value="template.id_whatsapp_template"
                >
                  {{ template.template_name }}
                  <span v-if="template.whatsapp_template_status === 'pending'"> (Aguardando aprovação)</span>
                </option>
              </select>
              <div class="form-text">Selecione um template aprovado para iniciar a conversa</div>
            </div>

            <div v-if="selectedTemplate && templateParameters.length > 0" class="mb-3">
              <label class="form-label">Variáveis do Template</label>
              <div v-for="(param, index) in templateParameters" :key="index" class="mb-2">
                <label class="form-label small required">{{ param.label || `Variável ${index + 1}` }}</label>
                <input
                  type="text"
                  v-model="form.template_parameters[index]"
                  @input="$forceUpdate()"
                  class="form-control form-control-sm"
                  :placeholder="`Digite o valor para ${param.label || 'variável ' + (index + 1)}`"
                  required
                />
              </div>
            </div>

            <div v-if="selectedTemplate" class="mb-3">
              <label class="form-label">Preview da Mensagem</label>
              <div class="card bg-light">
                <div class="card-body">
                  <div class="message-preview" v-html="formatPreviewMessage(previewMessage)"></div>
                </div>
              </div>
              <div class="form-text">Esta é a mensagem que será enviada ao paciente</div>
            </div>

            <div v-if="approvedTemplates.length === 0" class="alert alert-warning">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Nenhum template aprovado encontrado. Você precisa criar e aprovar um template antes de iniciar uma conversa.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetForm">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="loading || approvedTemplates.length === 0">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Iniciar Conversa
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Modal from "@/helpers/modal";
import whatsappService from "@/service/whatsappService";
import { mask } from "vue-the-mask";

export default {
  name: "NewChatModal",
  directives: {
    mask,
  },
  props: {
    placeId: {
      type: Number,
      required: true,
    },
  },
  emits: ["chat-created"],
  data() {
    return {
      loading: false,
      templates: [],
      form: {
        country_code: "BR",
        phone_number: "",
        template_id: null,
        template_parameters: [],
      },
      countries: [
        { code: "BR", name: "Brasil", dialCode: "+55", flag: "🇧🇷", placeholder: "(19) 99999-9999", mask: ["(##) ####-####", "(##) #####-####"] },
        { code: "US", name: "Estados Unidos", dialCode: "+1", flag: "🇺🇸", placeholder: "(555) 123-4567", mask: ["(###) ###-####"] },
        { code: "AR", name: "Argentina", dialCode: "+54", flag: "🇦🇷", placeholder: "(11) 1234-5678", mask: ["(##) ####-####"] },
        { code: "CL", name: "Chile", dialCode: "+56", flag: "🇨🇱", placeholder: "(2) 1234-5678", mask: ["# ####-####"] },
        { code: "CO", name: "Colômbia", dialCode: "+57", flag: "🇨🇴", placeholder: "(1) 123-4567", mask: ["# ###-####"] },
        { code: "MX", name: "México", dialCode: "+52", flag: "🇲🇽", placeholder: "(55) 1234-5678", mask: ["(##) ####-####"] },
        { code: "PT", name: "Portugal", dialCode: "+351", flag: "🇵🇹", placeholder: "912 345 678", mask: ["### ### ###"] },
        { code: "ES", name: "Espanha", dialCode: "+34", flag: "🇪🇸", placeholder: "612 34 56 78", mask: ["### ## ## ##"] },
      ],
    };
  },
  computed: {
    selectedCountry() {
      return this.countries.find(c => c.code === this.form.country_code) || this.countries[0];
    },
    approvedTemplates() {
      return this.templates.filter(
        (t) => t.is_whatsapp_template && t.whatsapp_template_status === "approved"
      );
    },
    selectedTemplate() {
      return this.templates.find((t) => t.id_whatsapp_template === this.form.template_id);
    },
    templateParameters() {
      if (!this.selectedTemplate) return [];
      
      const content = this.selectedTemplate.template_content || "";
      const matches = content.match(/\{\{(\d+)\}\}/g);
      if (!matches) return [];

      return matches.map((match, index) => {
        const num = match.replace(/\{\{|\}\}/g, "");
        return {
          index: parseInt(num) - 1,
          label: `Variável ${num}`,
        };
      });
    },
    previewMessage() {
      if (!this.selectedTemplate) return "";
      
      let content = this.selectedTemplate.template_content || "";
      
      if (this.templateParameters.length > 0 && this.form.template_parameters.length > 0) {
        this.templateParameters.forEach((param, index) => {
          const value = this.form.template_parameters[index] || `{{${param.index + 1}}}`;
          content = content.replace(`{{${param.index + 1}}}`, value);
        });
      }
      
      return content;
    },
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    async loadTemplates() {
      try {
        this.templates = await whatsappService.getTemplates(this.placeId);
      } catch (error) {
        Alert.error("Erro ao carregar templates");
      }
    },
    onTemplateChange() {
      if (this.selectedTemplate) {
        const paramCount = this.templateParameters.length;
        this.form.template_parameters = new Array(paramCount).fill("");
      } else {
        this.form.template_parameters = [];
      }
    },
    async handleSubmit() {
      if (!this.form.phone_number || !this.form.template_id) {
        Alert.error("Preencha todos os campos obrigatórios");
        return;
      }

      if (this.templateParameters.length > 0) {
        const missingParams = this.templateParameters.filter((param, index) => {
          const value = this.form.template_parameters[index];
          return !value || !value.trim();
        });

        if (missingParams.length > 0) {
          Alert.error("Preencha todas as variáveis do template");
          return;
        }
      }

      this.loading = true;

      try {
        let phoneNumber = this.form.phone_number.replace(/\D/g, "");
        const countryCode = this.selectedCountry.dialCode.replace('+', '');
        
        if (phoneNumber.length > 0 && !phoneNumber.startsWith(countryCode)) {
          phoneNumber = countryCode + phoneNumber;
        }
        
        const chatResponse = await whatsappService.createChat(this.placeId, phoneNumber);
        const chat = chatResponse.data.chat;

        const parameters = this.templateParameters.map((param, index) => {
          return this.form.template_parameters[index]?.trim() || '';
        });

        const messageResponse = await whatsappService.sendInitialMessage(
          this.placeId,
          chat.id,
          this.form.template_id,
          parameters
        );

        Modal.toggle("#newChatModal");
        this.resetForm();
        
        Alert.success("Conversa iniciada com sucesso!");
        this.$emit("chat-created", chat);
      } catch (error) {
        console.error("Error creating chat:", error);
        Alert.error(
          error.response?.data?.message || "Erro ao iniciar conversa"
        );
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        country_code: "BR",
        phone_number: "",
        template_id: null,
        template_parameters: [],
      };
    },
    formatPreviewMessage(message) {
      if (!message) return "";
      
      let formatted = message
        .replace(/\r\n/g, "<br>")
        .replace(/\n/g, "<br>")
        .replace(/\r/g, "<br>")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>");
      
      return formatted;
    },
  },
};
</script>

<style scoped>
.required::after {
  content: " *";
  color: red;
}

.message-preview {
  word-wrap: break-word;
  line-height: 1.5;
  color: #333;
}
</style>

